import { FunctionComponent } from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";

import { useTranslation } from "react-i18next";

import Checked from "@assets/icons/checked.svg?url";
import Refresh from "@assets/icons/refresh.svg?url";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { DeepResearchStatus } from "../utils/getDeepResearchStatus";

const TOOLTIP_KEYS: Record<DeepResearchStatus, string> = {
	not_done: "messages.deepResearchPending",
	queued: "profileTracker.deepResearch.chipQueued",
	running: "messages.deepResearchRunning",
	failed: "profileTracker.deepResearch.chipFailed",
	done: "messages.deepResearchReady",
};

/** Border + tinted background that match the neighbouring tags (InfoTag/CampaignTag) per status. */
const CONTAINER_STYLES: Record<DeepResearchStatus, string> = {
	not_done: "border-blue-action/50 bg-blue-action/10",
	queued: "border-blue-action/50 bg-blue-action/10",
	running: "border-blue-action/50 bg-blue-action/10",
	failed: "border-status-error/50 bg-status-error-light/50",
	done: "border-status-success/50 bg-status-success-light/50",
};

/** Base glyph colour follows the status family, like the icons on the neighbouring tags. */
const ICON_COLORS: Record<DeepResearchStatus, string> = {
	not_done: "text-blue-action",
	queued: "text-blue-action",
	running: "text-blue-action",
	failed: "text-status-error",
	done: "text-status-success",
};

/**
 * Status indicator shown on lead cards when the lead's list has a deep research column.
 * Rendered as a bordered chip (matching AiSnippetStatusIcon and the neighbouring tags) holding a
 * research glyph with a corner status badge — blue refresh = not run yet, pulsing grey dot =
 * queued, spinning blue refresh = running, red mark = the last run failed, green check = done.
 */
export const DeepResearchStatusChip: FunctionComponent<{
	status: DeepResearchStatus;
	// Compact lead card (PREC-3850): fixed 24px chip height so the chip row aligns, no stray margin.
	compact?: boolean;
}> = ({ status, compact = false }) => {
	const { t } = useTranslation();

	return (
		<Tooltip content={t(TOOLTIP_KEYS[status])}>
			<span
				className={classNames(
					"inline-flex items-center justify-center rounded-md border transition-colors",
					compact ? "h-6 px-1.5" : "mb-1 px-2 py-1",
					CONTAINER_STYLES[status]
				)}
			>
				<span className="relative inline-flex h-[20px] w-[20px] items-center justify-center">
					{status === "done" && (
						<img alt="done" className="absolute right-[-1px] top-[-1px] h-3 w-3" src={Checked} />
					)}
					{status === "not_done" && (
						<img
							alt="not done"
							className="absolute right-[-1px] top-[-1px] h-3 w-3"
							src={Refresh}
						/>
					)}
					{status === "queued" && (
						<span
							className="absolute right-0 top-0 h-2 w-2 animate-pulse rounded-full bg-text-tertiary"
							data-testid="deep-research-chip-queued"
						/>
					)}
					{status === "failed" && (
						<span
							className="absolute right-[-2px] top-[-2px] flex h-3 w-3 items-center justify-center rounded-full bg-status-error text-[9px] font-bold leading-none text-white"
							data-testid="deep-research-chip-failed"
						>
							!
						</span>
					)}
					{status === "running" && (
						<img
							alt="running"
							className="absolute right-[-1px] top-[-1px] h-3 w-3 animate-spin"
							src={Refresh}
						/>
					)}
					<Icon className={classNames("h-4 w-4", ICON_COLORS[status])} icon={faMagnifyingGlass} />
				</span>
			</span>
		</Tooltip>
	);
};
